import _ from 'lodash';

import {
  AiChatMessageRole,
  AiChatMessageStatus,
  IN_FLIGHT_MESSAGE_STATUSES,
  ToolActivityStatus,
} from 'constants/aiChat';

export const applyAssistantActivity = state => {
  if (!state.isAwaitingAssistantResponse) {
    return state;
  }

  return {
    ...state,
    awaitingAssistantLastActivityAt: new Date().toISOString(),
    isAssistantSlow: false,
  };
};

const createStreamingAssistantMessage = ({ messageId, sessionId, token, timestamp }) => ({
  id: messageId,
  sessionId: sessionId ?? null,
  role: AiChatMessageRole.ASSISTANT,
  status: AiChatMessageStatus.STREAMING,
  content: token ?? '',
  isStreaming: true,
  timestamp,
});

export const ensureStreamingAssistantPlaceholder = (messages, payload) => {
  const { messageId, sessionId } = payload ?? {};

  if (_.isNil(messageId)) {
    return messages;
  }

  const existingIndex = messages.findIndex(message => message.id === messageId);

  if (existingIndex !== -1) {
    return messages;
  }

  const placeholder = createStreamingAssistantMessage({
    messageId,
    sessionId,
    token: '',
    timestamp: new Date().toISOString(),
  });

  return [...messages, placeholder];
};

export const applyResponseTokenToMessages = (messages, payload) => {
  const { messageId, sessionId, token } = payload ?? {};

  if (_.isNil(messageId)) {
    return messages;
  }

  const messageIndex = messages.findIndex(message => message.id === messageId);
  const timestamp = new Date().toISOString();

  if (messageIndex === -1) {
    const newMessage = createStreamingAssistantMessage({
      messageId,
      sessionId,
      token,
      timestamp,
    });

    return [...messages, newMessage];
  }

  const existing = messages[messageIndex];
  const nextMessages = [...messages];

  nextMessages[messageIndex] = {
    ...existing,
    content: `${existing.content ?? ''}${token ?? ''}`,
    isStreaming: true,
    status: AiChatMessageStatus.STREAMING,
  };

  return nextMessages;
};

export const reconcileSentMessage = (messages, serverMessage, clientTempId) => {
  if (_.isNil(clientTempId)) {
    return [...messages, serverMessage];
  }

  const optimisticIndex = messages.findIndex(message => message.id === clientTempId);

  if (optimisticIndex === -1) {
    return [...messages, serverMessage];
  }

  const nextMessages = [...messages];
  const optimistic = nextMessages[optimisticIndex];

  nextMessages[optimisticIndex] = { ...optimistic, ...serverMessage };

  return nextMessages;
};

export const markOptimisticMessageFailed = (messages, clientTempId) => {
  if (_.isNil(clientTempId)) {
    return messages;
  }

  const optimisticIndex = messages.findIndex(message => message.id === clientTempId);

  if (optimisticIndex === -1) {
    return messages;
  }

  const nextMessages = [...messages];
  const optimistic = nextMessages[optimisticIndex];

  nextMessages[optimisticIndex] = { ...optimistic, status: AiChatMessageStatus.FAILED };

  return nextMessages;
};

const buildToolActivityId = (messages, relatedMessageId, toolName) => {
  const prefix = `tool-${relatedMessageId}-${toolName}`;
  const existingCount = messages.filter(m => _.startsWith(m.id, `${prefix}-`)).length;

  return `${prefix}-${existingCount}`;
};

export const applyToolStartedToMessages = (messages, payload) => {
  const { messageId, toolName, friendlyName } = payload ?? {};

  if (_.isNil(messageId) || _.isNil(toolName)) {
    return messages;
  }

  const toolActivity = {
    id: buildToolActivityId(messages, messageId, toolName),
    role: AiChatMessageRole.TOOL,
    status: ToolActivityStatus.RUNNING,
    toolName,
    friendlyName: friendlyName ?? toolName,
    relatedMessageId: messageId,
    timestamp: new Date().toISOString(),
  };

  return [...messages, toolActivity];
};

const findLatestRunningToolIndex = (messages, relatedMessageId, toolName) =>
  _.findLastIndex(
    messages,
    m =>
      m.role === AiChatMessageRole.TOOL &&
      m.relatedMessageId === relatedMessageId &&
      m.toolName === toolName &&
      m.status === ToolActivityStatus.RUNNING,
  );

export const applyToolCompletedToMessages = (messages, payload) => {
  const { messageId, toolName, summary, result } = payload ?? {};

  if (_.isNil(messageId) || _.isNil(toolName)) {
    return messages;
  }

  const index = findLatestRunningToolIndex(messages, messageId, toolName);

  if (index === -1) {
    return messages;
  }

  const existing = messages[index];
  const nextMessages = [...messages];

  nextMessages[index] = {
    ...existing,
    status: ToolActivityStatus.COMPLETED,
    summary: summary ?? null,
    result: result ?? null,
    completedAt: new Date().toISOString(),
  };

  return nextMessages;
};

export const applyToolFailedToMessages = (messages, payload) => {
  const { messageId, toolName, error } = payload ?? {};

  if (_.isNil(messageId) || _.isNil(toolName)) {
    return messages;
  }

  const index = findLatestRunningToolIndex(messages, messageId, toolName);

  if (index === -1) {
    return messages;
  }

  const existing = messages[index];
  const nextMessages = [...messages];

  nextMessages[index] = {
    ...existing,
    status: ToolActivityStatus.FAILED,
    error: error ?? null,
    completedAt: new Date().toISOString(),
  };

  return nextMessages;
};

export const applyResponseCompleteToMessages = (messages, payload) => {
  const { messageId, finalContent } = payload ?? {};

  if (_.isNil(messageId)) {
    return messages;
  }

  const messageIndex = messages.findIndex(message => message.id === messageId);

  if (messageIndex === -1) {
    return messages;
  }

  const existing = messages[messageIndex];
  const nextMessages = [...messages];
  const resolvedContent = _.isNil(finalContent) ? existing.content : finalContent;
  const completedAt = new Date().toISOString();

  nextMessages[messageIndex] = {
    ...existing,
    content: resolvedContent,
    isStreaming: false,
    status: AiChatMessageStatus.COMPLETED,
    completedAt,
  };

  return nextMessages;
};

const buildFailedAssistantPlaceholder = ({ relatedMessageId, error, canRetry }) => ({
  id: `failed-${relatedMessageId ?? 'timeout'}-${Date.now()}`,
  role: AiChatMessageRole.ASSISTANT,
  status: AiChatMessageStatus.FAILED,
  content: '',
  error: error ?? null,
  canRetry: canRetry !== false,
  relatedUserMessageId: relatedMessageId ?? null,
  timestamp: new Date().toISOString(),
});

export const applyMessageFailedToMessages = (messages, payload) => {
  const { messageId, error, canRetry } = payload ?? {};

  const existingIndex = _.isNil(messageId)
    ? -1
    : _.findLastIndex(
        messages,
        message => message.id === messageId && message.role === AiChatMessageRole.ASSISTANT,
      );

  if (existingIndex !== -1) {
    const nextMessages = [...messages];
    const existing = nextMessages[existingIndex];

    nextMessages[existingIndex] = {
      ...existing,
      status: AiChatMessageStatus.FAILED,
      isStreaming: false,
      error: error ?? existing.error ?? null,
      canRetry: canRetry !== false,
    };

    return nextMessages;
  }

  const hasExistingFailedPlaceholder = messages.some(
    message =>
      message.role === AiChatMessageRole.ASSISTANT &&
      message.status === AiChatMessageStatus.FAILED &&
      message.relatedUserMessageId === (messageId ?? null),
  );

  if (hasExistingFailedPlaceholder) {
    return messages;
  }

  return [
    ...messages,
    buildFailedAssistantPlaceholder({
      relatedMessageId: messageId,
      error,
      canRetry,
    }),
  ];
};

export const applySessionStoppedToMessages = (messages, payload) => {
  const stoppedMessageIds = payload?.stoppedMessageIds ?? null;
  const stoppedIdSet =
    Array.isArray(stoppedMessageIds) && stoppedMessageIds.length > 0
      ? new Set(stoppedMessageIds)
      : null;

  return messages.map(message => {
    const isTargeted = stoppedIdSet ? stoppedIdSet.has(message.id) : true;
    const isInFlight = IN_FLIGHT_MESSAGE_STATUSES.has(message.status);

    if (!isTargeted || !isInFlight) {
      return message;
    }

    return {
      ...message,
      status: AiChatMessageStatus.FAILED,
      isStreaming: false,
      isCancelled: true,
      canRetry: false,
    };
  });
};

export const removeFailedAssistantPlaceholderForMessage = (messages, userMessageId) => {
  if (_.isNil(userMessageId)) {
    return messages;
  }

  return messages.filter(
    message =>
      !(
        message.role === AiChatMessageRole.ASSISTANT &&
        message.status === AiChatMessageStatus.FAILED &&
        message.relatedUserMessageId === userMessageId
      ),
  );
};

export const applyMessageFeedbackOutcome = (messages, payload) => {
  if (_.isNil(payload?.messageId)) {
    return messages;
  }

  const messageIndex = messages.findIndex(message => message.id === payload.messageId);

  if (messageIndex === -1) {
    return messages;
  }

  const updatedMessages = [...messages];
  updatedMessages[messageIndex] = {
    ...updatedMessages[messageIndex],
    userOutcome: payload.outcome,
  };

  return updatedMessages;
};

export const applyCitationsFlagToMessages = (messages, payload) => {
  const { messageId, citations } = payload ?? {};

  if (_.isNil(messageId) || _.isEmpty(citations)) {
    return messages;
  }

  const messageIndex = messages.findIndex(message => message.id === messageId);

  if (messageIndex === -1) {
    return messages;
  }

  const existing = messages[messageIndex];

  if (existing.hasCitations) {
    return messages;
  }

  const nextMessages = [...messages];
  nextMessages[messageIndex] = {
    ...existing,
    hasCitations: true,
  };

  return nextMessages;
};
